import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';

interface Supervisor {
  user_id: string;
  email: string;
}

interface QuickAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: { id: string; first_name: string; last_name: string } | null;
  onSuccess: () => void;
}

export function QuickAssignDialog({
  open,
  onOpenChange,
  patient,
  onSuccess,
}: QuickAssignDialogProps) {
  const { user } = useAdminAuth();
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingSupervisors, setLoadingSupervisors] = useState(true);

  useEffect(() => {
    if (open && patient) {
      loadSupervisors();
      setSelectedSupervisor('');
    }
  }, [open, user?.agency_id, patient]);

  const loadSupervisors = async () => {
    if (!user?.agency_id) return;

    setLoadingSupervisors(true);
    try {
      // Get all users with supervisor role in the agency
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, email')
        .eq('agency_id', user.agency_id)
        .eq('is_active', true);

      // Get supervisor roles
      const { data: roles } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'supervisor');

      const supervisorUserIds = new Set(roles?.map(r => r.user_id) || []);
      const supervisorList = (profiles || [])
        .filter(p => supervisorUserIds.has(p.user_id))
        .map(p => ({
          user_id: p.user_id,
          email: p.email,
        }));

      setSupervisors(supervisorList);
    } catch (error) {
      console.error('Error loading supervisors:', error);
      toast.error('Failed to load supervisors');
    } finally {
      setLoadingSupervisors(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedSupervisor || !patient) return;

    setLoading(true);
    try {
      // Check if already assigned
      const { data: existing } = await supabase
        .from('patient_assignments')
        .select('id')
        .eq('patient_id', patient.id)
        .eq('staff_user_id', selectedSupervisor)
        .is('revoked_at', null)
        .maybeSingle();

      if (existing) {
        toast.info('Patient is already assigned to this supervisor');
        onOpenChange(false);
        return;
      }

      // Create new assignment
      const { error } = await supabase
        .from('patient_assignments')
        .insert({
          patient_id: patient.id,
          staff_user_id: selectedSupervisor,
          assigned_by: user?.id || null,
        });

      if (error) throw error;

      toast.success(`Assigned ${patient.first_name} ${patient.last_name} to supervisor`);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error assigning patient:', error);
      toast.error('Failed to assign patient');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Quick Assign Supervisor
          </DialogTitle>
          <DialogDescription>
            Assign {patient?.first_name} {patient?.last_name} to a supervisor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="supervisor">Select Supervisor</Label>
            <Select
              value={selectedSupervisor}
              onValueChange={setSelectedSupervisor}
              disabled={loadingSupervisors}
            >
              <SelectTrigger id="supervisor">
                <SelectValue placeholder={loadingSupervisors ? 'Loading...' : 'Choose a supervisor'} />
              </SelectTrigger>
              <SelectContent>
                {supervisors.map((supervisor) => (
                  <SelectItem key={supervisor.user_id} value={supervisor.user_id}>
                    {supervisor.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!loadingSupervisors && supervisors.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No supervisors found. Add a supervisor first.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedSupervisor || loading || supervisors.length === 0}
          >
            {loading ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
